import type { DraftStatus } from '../../../../shared/enums';
import type { DraftWithLead } from '../../../../shared/types';
import { timeAgo } from '../../logic/dates';
import { btn, linkBtn } from '../../ui/components';
import { h, span } from '../../ui/dom';
import { LANGUAGE_LABELS } from '../../ui/labels';

/** A draft's pieces, shared by the phone's expanding cards and the desktop's list + detail (D65). */

const TRIGGER_LABEL: Record<string, string> = {
  contract_expiring: 'Contract',
  post_visit: 'Follow-up',
  meeting_confirm: 'Meeting',
  feature_launched: 'Feature news',
};

export const milestoneLabel = (d: DraftWithLead): string => {
  if (d.milestone_key === 'contract_30') return 'Contract · 30d';
  if (d.milestone_key === 'contract_60') return 'Contract · 60d';
  return TRIGGER_LABEL[d.trigger] ?? d.trigger;
};

export const draftMeta = (d: DraftWithLead): HTMLElement =>
  h(
    'div',
    { class: 'draft__meta' },
    span('posbadge', milestoneLabel(d)),
    span('posbadge', LANGUAGE_LABELS[d.language]),
    d.status !== 'pending' ? span(`posbadge draft__status--${d.status}`, d.status) : null,
    span('mono muted', ` ${timeAgo(d.created_at)}`),
  );

export interface DraftActions {
  copy: (text: string, what: string) => void;
  setStatus: (status: DraftStatus) => void;
}

export const draftBody = (d: DraftWithLead, act: DraftActions): HTMLElement =>
  h(
    'div',
    { class: 'draft__body' },
    h('div', { class: 'draft__subject' }, d.subject),
    h('pre', { class: 'draft__text' }, d.body),
    h(
      'div',
      { class: 'draft__actions' },
      btn('Copy subject', { variant: 'subtle', size: 'sm', onClick: () => act.copy(d.subject, 'Subject') }),
      btn('Copy body', { variant: 'subtle', size: 'sm', onClick: () => act.copy(d.body, 'Body') }),
      btn('Copy both', {
        variant: 'primary',
        size: 'sm',
        onClick: () => act.copy(`Subject: ${d.subject}\n\n${d.body}`, 'Email'),
      }),
      d.status !== 'dismissed'
        ? btn('Dismiss', { variant: 'ghost', size: 'sm', onClick: () => act.setStatus('dismissed') })
        : btn('Restore', { variant: 'ghost', size: 'sm', onClick: () => act.setStatus('pending') }),
    ),
    h(
      'div',
      { class: 'draft__leadlink' },
      linkBtn('Open lead', `#/leads/${encodeURIComponent(d.lead_id)}`, { variant: 'ghost', size: 'sm' }),
    ),
  );
